package com.qrkontaq.ui.admin

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.qrkontaq.data.dto.CreateRestaurantRequestDto
import com.qrkontaq.data.repository.AdminRepository
import com.qrkontaq.ui.components.forms.TextInput
import com.qrkontaq.ui.theme.Brand
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.silk.components.text.SpanText
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.px

/**
 * Creating a restaurant when the owner is already signed in but has no venue
 * (e.g. after deleting the last one). Shares the emerald split-screen frame with
 * the sign-up form so the flow stays consistent — name + slug + address in one step.
 */
@Composable
fun WizardView(
    repository: AdminRepository,
    scope: CoroutineScope,
    onCreated: (restaurantId: String) -> Unit,
    onCancel: (() -> Unit)? = null,
) {
    var name by remember { mutableStateOf("") }
    var slug by remember { mutableStateOf("") }
    var slugTouched by remember { mutableStateOf(false) }
    var error by remember { mutableStateOf<String?>(null) }
    var busy by remember { mutableStateOf(false) }

    val effectiveSlug = if (slugTouched) slug else slugPreview(name)
    val fieldMod = Modifier.margin(bottom = 12.px)

    AuthScaffold {
        AuthFormHeader(AdminT.authWizardSub)

        TextInput(
            value = name,
            onValueChange = { name = it },
            modifier = fieldMod,
            label = AdminT.authShopName,
            placeholder = AdminT.authShopNamePh,
        )
        TextInput(
            value = effectiveSlug,
            onValueChange = {
                slugTouched = true
                slug = it.lowercase()
            },
            modifier = Modifier.margin(bottom = 5.px),
            label = AdminT.authDomain,
            placeholder = AdminT.authDomainPh,
        )
        val previewFilled = effectiveSlug.isNotEmpty()
        SpanText(
            "${effectiveSlug.ifEmpty { AdminT.authDomainPh }}.qrkontaq.com",
            Modifier
                .fillMaxWidth()
                .fontSize(13.px)
                .fontWeight(FontWeight.SemiBold)
                .color(if (previewFilled) Brand.Emerald else Brand.TextSecondary)
                .margin(bottom = 12.px),
        )
        AdminError(error)
        AdminButton(
            text = if (busy) "…" else AdminT.authCreateShop,
            enabled = !busy && name.isNotBlank() && effectiveSlug.length >= 3,
            modifier = Modifier.fillMaxWidth().margin(top = 12.px),
            onClick = {
                if (!SLUG_REGEX.matches(effectiveSlug)) {
                    error = AdminT.authErrDomainRules
                    return@AdminButton
                }
                busy = true
                error = null
                scope.launch {
                    val response =
                        runCatching {
                            repository.createRestaurant(
                                CreateRestaurantRequestDto(
                                    name = name,
                                    slug = effectiveSlug,
                                    language = adminLanguage.code,
                                ),
                            )
                        }.getOrNull()
                    busy = false
                    val created = response?.data
                    if (created != null) {
                        onCreated(created.id)
                    } else {
                        error = mapSlugError(response?.errors, fallback = AdminT.authErrDomainTaken)
                    }
                }
            },
        )
        if (onCancel != null) {
            AdminButton(
                text = AdminT.cancel,
                secondary = true,
                modifier = Modifier.fillMaxWidth().margin(top = 12.px),
                onClick = { onCancel() },
            )
        }
    }
}
